import { useEffect } from 'react';

import { useMytaverseEvent } from '@core/dashboard/providers';
import {
  LastWebsocketJsonMessageType,
  WebsocketAction,
} from '@interfaces/webSocketConnectionInfo';

/**
 * A branding's saved real-time entities changed — possibly by another admin.
 *
 * Only the revision is bumped, not the registry: the fetch is admin-scoped, so
 * a participant without the editor open must not issue it, and an editor that
 * is open has unsaved rows of its own that a wholesale replace would discard.
 * The open editor reloads with whichever branding it has open, so the message's
 * own `brandingId` needs no matching here.
 *
 * @param lastJsonMessage - Latest message off the dashboard websocket. Anything
 * other than `SPAWNED_ENTITIES_UPDATED`, or a change in a room this participant
 * is not in, is ignored.
 */
export const useHandleSpawnedEntitiesUpdatedMsg = (
  lastJsonMessage: LastWebsocketJsonMessageType,
) => {
  const { currentRoom, bumpRealtimeEntitiesRevision } = useMytaverseEvent();

  const currentRoomId = currentRoom?.roomId;

  useEffect(() => {
    if (!lastJsonMessage) {
      return;
    }

    const { action, roomId } = lastJsonMessage;

    if (action !== WebsocketAction.SpawnedEntitiesUpdated) {
      return;
    }

    if (!roomId || roomId !== currentRoomId) {
      return;
    }

    bumpRealtimeEntitiesRevision();
  }, [lastJsonMessage, currentRoomId, bumpRealtimeEntitiesRevision]);
};
